import { Fragment, useCallback, useRef, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { IonButton, IonContent, IonIcon, IonInput, IonItem, IonLabel, IonList, IonPopover, IonSegment, IonSegmentButton, IonSpinner, IonText, useIonToast } from "@ionic/react";
import { PeriodDetail, TimetableType } from "../../hooks/useTimetablePeriods";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import CoverTeacherSelect, { SelectedCoverTeacher } from "./CoverTeacherSelect";
import { addCircle } from "ionicons/icons";
import { useTeacherClasses } from "../../hooks/useTeacherClasses";
import { formatDate } from "../../utils";
import TimeShort from "./TimeShort";
import { LessonDetail, LessonEventRequest, useLessonDetail } from "../../hooks/useLessonDetail";
import PeriodSelector from "./PeriodSelector";
import { toInteger } from "lodash";
import ClosePopoverButton from "./ClosePopoverButton";

import "./AddLessonButton.css";

interface ContainerProps extends RouteComponentProps<any> {
  timetableType?: TimetableType;
  timetableDate: string | null;
  timetablePeriod?: string | null;
  onAddLesson?: () => void;
  onAddPeriods?: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
const AddLessonButton: React.FC<ContainerProps> = ({
  timetableType,
  timetableDate,
  timetablePeriod: _timetablePeriod,
  onAddLesson,
  onAddPeriods,
}) => {

  const [selectedTeacher, setSelectedTeacher] = useState<any>(null);
  const [selectedPeriods, setSelectedPeriods] = useState<PeriodDetail[]>([]);
  const [workDescription, setWorkDescription] = useState<string>("");
  const [mode, setMode] = useState<any>("Existing");
  const popover = useRef<HTMLIonPopoverElement>(null);

  const [showToast] = useIonToast();

  const {
    lessonSaving,
    updateLesson,
    createLesson,
    createLessonEvent,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isUpdateError,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updateError,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    clearErrors,
  } = useLessonDetail("");

  const updateLessonDetail = useCallback(
    async (newLesson: LessonDetail) => {
      const { _id } = newLesson;

      // If there is no lesson id, then create a new lesson
      if (!_id) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const res = await createLesson(newLesson);
        return;
      }

      // Otherwise update the existing lesson
      updateLesson(newLesson);
    },
    [createLesson, updateLesson]
  );

  const handleTeacherSelected = (teacher: any, inst?: any) => {
    console.log("teacher selected", teacher);
    const teacherData = inst?.props?.data?.find(
      (d: any) => d?.value === teacher?.value
    );
    const t = {
      _id: teacher.value,
      Name: teacher?.valueText,
      data: teacherData,
    };
    setSelectedTeacher(t);
  }

  const handleClearSelection = () => {
    setSelectedTeacher("");
    setSelectedPeriods([]);
  }

  const handleSelectClass = async (cl: any) => {
    console.log("class selected", cl);
    let newLesson: LessonDetail = {
      _id: null,
      Class: {
        PeriodClassId: cl?.PeriodClassId,
        PeriodId: cl?.PeriodId,
        ClassId: cl?.ClassId,
      },
      LessonStartTime: cl?.StartDateTime,
      LessonEndTime: cl?.EndDateTime,
      Absence: {
        _id: null,  // Not and absence, just recording the original teacher's details
        Teacher: {
          _id: selectedTeacher?._id,
          ContactId: selectedTeacher?.data?.ForeignKeys?.Edumate?.ContactId,
          Name: selectedTeacher?.Name,
        },
      },

      // Room: {
      //   ForeignKey: cl?.RoomId,
      //   Short: cl?.RoomShort,
      // },
    };
    await updateLessonDetail(newLesson);
    showToast({
      message: "Lesson Added to Cover!",
      color: "success", position: "middle", duration: 4000
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onAddLesson && setTimeout(() => onAddLesson(), 3000);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { teacherClasses, teacherClassesLoaded, isLoading: classesLoading } = useTeacherClasses(timetableDate, selectedTeacher?._id);

  const handleModeSelected = (e: any) => {
    handleClearSelection();
    setMode(e?.detail?.value);
  }

  const handleAddPeriodEvents = async () => {
    if (selectedPeriods.length === 0) {
      showToast({
        message: "No Periods Selected!",
        color: "danger", position: "middle", duration: 4000
      });
      return;
    }

    if (!workDescription) {
      showToast({
        message: "No Work Description!",
        color: "danger", position: "middle", duration: 4000
      });
      return;
    }

    if (!timetableDate) {
      showToast({
        message: "No Date Selected!",
        color: "danger", position: "middle", duration: 4000
      });
      return;
    }

    const periodIds = selectedPeriods.map((p) => toInteger(p.PeriodId));
    const teacherIds = selectedTeacher && selectedTeacher?._id ? [selectedTeacher?._id] : [];
    const req: LessonEventRequest = {
      Date: formatDate(timetableDate),
      Title: workDescription,
      Periods: periodIds,
      Teachers: teacherIds,
    };
    await createLessonEvent(req);

    showToast({
      message: "Period Events Added to Cover!",
      color: "success", position: "middle", duration: 4000
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onAddPeriods && setTimeout(() => onAddPeriods(), 3000);
  }

  const handleOnPresent = () => {
    handleClearSelection();
  }

  return (
    <Fragment>

      <IonButton
        slot="end"
        size="small"
        className="ion-text-capitalize add-lesson-button"
        id="add-new-lesson"
      >
        Add Lesson
        <IonIcon icon={addCircle} slot="start" />
      </IonButton>
      <IonPopover ref={popover} trigger="add-new-lesson" triggerAction="click" className="add-lesson-popover" onDidPresent={handleOnPresent}>
        <IonContent class="ion-padding">
          <IonItem lines="none">
            <ClosePopoverButton popover={popover} />
          </IonItem>
          <IonSegment
            color="primary"
            value={mode}
            mode="ios"
            onIonChange={handleModeSelected}
          >
            <IonSegmentButton value="Existing" mode="ios">
              <IonLabel>{"Cover Existing Lesson(s)"}
              </IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="New" mode="ios">
              <IonLabel>{"Add New Activity"}</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {mode === "Existing" &&
            <Fragment>

              <IonItem lines="none"><h3>Who Do You Want to Cover?</h3>
                {(selectedTeacher || selectedPeriods.length > 0) &&
                  <IonButton size="small" fill="clear" onClick={handleClearSelection} slot="end">Clear Selection</IonButton>}
              </IonItem>
              <CoverTeacherSelect
                onChange={handleTeacherSelected}
                searchDate={timetableDate ? formatDate(timetableDate) : ""}
                placeholder="Find current teacher..."
                display="inline"
              />
            </Fragment>
          }
          {selectedTeacher && mode === "Existing" &&
            <Fragment>
              <IonItem lines="none"><h3>Select Lessons to Cover</h3></IonItem>
              <IonList className="add-lesson-classlist">
                {selectedTeacher && teacherClassesLoaded && teacherClasses.map((cl: any) => (
                  <IonItem key={cl?.ClassId} onClick={() => handleSelectClass(cl)}>
                    <IonText slot="start">P {cl?.PeriodName}</IonText>
                    <IonLabel>{cl?.ClassNameLong}</IonLabel>
                    <IonText slot="end"><TimeShort time={cl?.StartTime} /></IonText>
                  </IonItem>
                ))}
              </IonList>
            </Fragment>
          }
          {mode === "New" &&
            <Fragment>
              <IonItem lines="none"><h3>Select Periods to Cover</h3>
                {(selectedTeacher || selectedPeriods.length > 0) &&
                  <IonButton size="small" fill="clear" onClick={handleClearSelection} slot="end">Clear Selection</IonButton>}
              </IonItem>
              {/* <TimetableSelector timetableType={timetableType ?? "Secondary"} onSelected={() => { }} /> */}
              <PeriodSelector timetableType={timetableType ?? "Secondary"} calendarDate={timetableDate ?? Date.now().toString()} onSelected={(period: PeriodDetail) => {
                // If already selected, remove it
                if (selectedPeriods.some((p) => p.PeriodId === period.PeriodId)) {
                  setSelectedPeriods(selectedPeriods.filter((p) => p.PeriodId !== period.PeriodId));
                  return;
                }
                else {
                  setSelectedPeriods([...selectedPeriods, period]);
                }
              }} lessons={[]} value={selectedPeriods} />
              <IonItem>
                <IonInput placeholder="Work description for teacher..." value={workDescription} onIonChange={(e) => {
                  setWorkDescription(e.detail.value!);
                }} />
              </IonItem>
              <IonItem lines="none"><h3>OPTIONAL: Select Teacher for these periods</h3></IonItem>
              <CoverTeacherSelect
                onChange={handleTeacherSelected}
                searchDate={timetableDate ? formatDate(timetableDate) : ""}
                placeholder="Find current teacher..."
                display="inline"
              />
            </Fragment>
          }
          <IonItem lines="none">
            <IonLabel>{selectedTeacher?.Name}</IonLabel>
            {mode === "New" && selectedPeriods.length > 0 && <IonButton
              onClick={handleAddPeriodEvents}
              slot="end"
              disabled={lessonSaving}
            >
              Add Period Events
            </IonButton>}
            {lessonSaving && <IonSpinner name="circular" color="primary" />}
          </IonItem>

        </IonContent>
      </IonPopover>
    </Fragment >
  );
};

export default withRouter(AddLessonButton);
